import { Checkbox, createForm, Flex, Select, TextInput, Text, PhoneInput, PhoneInputProps } from '@applyboard/crystal-ui'
import { ContactInformation, RelationshipType as ContactRelation } from '@applyboard/school-applications-types-lib'
import { isEmpty, omitBy, set } from 'lodash'
import { RawApplicationResponse, useUpdateApplication } from '../../../hooks'
import { emailValidate } from '../../../utils'
import { ApplicationFormCard } from './ApplicationFormCard'
import { RelationshipType, TimelineTabs } from '../../../utils/enums'
import { StudentApplication } from '../types'
import { ApplicationFormTitle } from './ApplicationFormTitle'
import { useGetTimelineItems } from '../../../hooks/useGetTimelineItems'
import { ComponentType } from 'react'

type ContactInformationFormFields = {
  phone: string
  alternatePhone: string
  email: string
  provideEmergencyContact: boolean
  emergencyName: string
  emergencyRelationship: string
  emergencyPhone: string
  emergencyEmail: string
}

const { Form, Field, useFieldValues } = createForm<ContactInformationFormFields>()

type ContactInformationTabProps = {
  disabled?: boolean
  application: StudentApplication
  onSuccess: (response?: RawApplicationResponse) => void
  onError: (err: Error) => void
  currentIntakeAvailable?: boolean
  onIntakeUnavailable?: () => void
  onPreviousTabButton?: () => void
  onNextTabButton?: () => void
}

export function ContactInformationTab(props: Readonly<ContactInformationTabProps>) {
  const { isUpdatingApplication, updateApplication } = useUpdateApplication({
    id: props.application.id,
  })
  const { cardNumber, lastTimeline: timelineLength } = useGetTimelineItems(props.application, TimelineTabs.CONTACT_INFORMATION)

  return (
    <Form
      defaultValues={{
        phone:
          props.application?.attributes?.personalInformation?.contactInformation?.phoneNumber ||
          '',
        alternatePhone:
          props.application?.attributes?.personalInformation?.contactInformation
            ?.alternatePhoneNumber || '',
        email: props.application.attributes?.personalInformation?.contactInformation?.email || '',
        provideEmergencyContact: haveEmergencyContact(props.application),
        emergencyName:
          props.application?.attributes?.personalInformation?.emergencyContact?.name || '',
        emergencyRelationship:
          props.application?.attributes?.personalInformation?.emergencyContact?.relationship ||
          '',
        emergencyPhone:
          props.application?.attributes?.personalInformation?.emergencyContact?.phoneNumber || '',
        emergencyEmail:
          props.application?.attributes?.personalInformation?.emergencyContact?.email || '',
      }}
      onSubmit={data => {
        if (props.disabled) {
          props.onSuccess()
        } else if (props.onIntakeUnavailable && !props.currentIntakeAvailable) {
          props.onIntakeUnavailable()
        } else {
          const contactInformation: ContactInformation = {
            phoneNumber: data.phone,
            alternatePhoneNumber: data.alternatePhone,
            email: data.email,
          }
          const personalInformation = {
            contactInformation,
          }

          if (data.provideEmergencyContact) {
            set(personalInformation, 'emergencyContact', {
              name: data.emergencyName,
              relationship: data.emergencyRelationship as ContactRelation,
              email: data.emergencyEmail,
              phoneNumber: data.emergencyPhone,
            })
          } else {
            set(personalInformation, 'emergencyContact', null)
          }

          updateApplication(
            {
              attributes: {
                personalInformation,
              },
            },
            {
              onSuccess: props.onSuccess,
              onError: props.onError,
            },
          )
        }
      }}
    >
      <ApplicationFormCard
        cardNumber={cardNumber}
        totalCards={timelineLength}
        onPreviousTabButton={props.onPreviousTabButton}
        onNextTabButton={props.onNextTabButton}
        icon="☎️"
        title="Contact Information"
        isLoading={isUpdatingApplication}
        disabled={props.disabled}
      >
        <ContactInformationFields disabled={props.disabled} application={props.application} />
      </ApplicationFormCard>
    </Form>
  )
}

type ContactInformationFieldsProps = {
  disabled?: boolean
  application: StudentApplication
}

function ContactInformationFields(props: Readonly<ContactInformationFieldsProps>) {
  const { provideEmergencyContact, phone, emergencyPhone } = useFieldValues([
    'provideEmergencyContact',
    'phone',
    'emergencyPhone',
  ])

  return (
    <>
      <Text variant="bodyM">
        Please provide the student’s contact information.
      </Text>
      <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
          <Field
            as={PhoneInput as ComponentType<PhoneInputProps>}
            label="Phone number"
            name="phone"
            required={
              !props.disabled
                ? phone.length
                  ? 'Invalid phone number'
                  : 'Phone number is required'
                : false
            }
            disabled={props.disabled}
          />
        </Flex.Item>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
          <Field
            as={PhoneInput as ComponentType<PhoneInputProps>}
            label="Alternate phone number"
            name="alternatePhone"
            disabled={props.disabled}
          />
        </Flex.Item>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
          <Field
            // @ts-ignore
            as={TextInput}
            label="Email"
            name="email"
            required={
              !props.disabled
                ? 'Email is required'
                : false
            }
            validate={(value: string) => {
              if (!!value && !emailValidate(value)) {
                return 'This email is invalid.'
              }
              return true
            }}
            disabled={props.disabled} />
        </Flex.Item>
        {!props.disabled || haveEmergencyContact(props.application) ? (
          <Flex basis="100%" pt={{ xs: 6, sm: 0 }}>
            <ApplicationFormTitle
              variant="titleS"
              level={3}
              icon="🚨"
              title="Emergency Contact"
            />
          </Flex>
        ) : null}
        {!props.disabled ? (
          <Flex.Item basis={{ xs: '100%' }}>
            <Field
              // @ts-ignore
              as={Checkbox}
              label="I’d like to provide an emergency contact for the student"
              name="provideEmergencyContact"
            />
          </Flex.Item>
        ) : null}
        {provideEmergencyContact ? (
          <>
            <Flex.Item basis={{ xs: '100%' }}>
              <Field
                // @ts-ignore
                as={TextInput}
                label="Name"
                name="emergencyName"
                helpText="Full name of student's emergency contact."
                disabled={props.disabled}
                required={!props.disabled && provideEmergencyContact ? 'Name is required' : false}
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              {/* //TODO: This field becomes a Select and use RelationshipCode from Application. Update with Aurora lastest changes */}
              <Field
                as={Select}
                label="Relationship"
                name="emergencyRelationship"
                disabled={props.disabled}
                appearance="styled"
                required={
                  !props.disabled && provideEmergencyContact ? 'Relationship is required' : false
                }
              >
                <Select.Option label="Parent" value={RelationshipType.PARENT} />
                <Select.Option label="Guardian" value={RelationshipType.GUARDIAN} />
                <Select.Option label="Friend" value={RelationshipType.FRIEND} />
                <Select.Option label="Spouse" value={RelationshipType.SPOUSE} />
                <Select.Option label="Relative" value={RelationshipType.RELATIVE} />
                <Select.Option label="Other" value={RelationshipType.OTHER} />
              </Field>
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field
                as={PhoneInput as ComponentType<PhoneInputProps>}
                label="Phone number"
                name="emergencyPhone"
                disabled={props.disabled}
                required={
                  !props.disabled && provideEmergencyContact
                    ? emergencyPhone.length
                      ? 'Invalid phone number'
                      : 'Phone number is required'
                    : false
                }
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field
                // @ts-ignore
                as={TextInput}
                label="Email"
                name="emergencyEmail"
                validate={(value: string) => {
                  if (!!value && !emailValidate(value)) {
                    return 'This email is invalid.'
                  }

                  return true
                }}
                disabled={props.disabled}
                required={!props.disabled && provideEmergencyContact ? 'Email is required' : false}
              />
            </Flex.Item>
          </>
        ) : null}
      </Flex>
    </>
  )
}

function haveEmergencyContact(application: StudentApplication) {
  if (!application?.attributes?.personalInformation?.emergencyContact) {
    return false
  }

  return !isEmpty(
    omitBy(application?.attributes?.personalInformation?.emergencyContact, v => !v?.length),
  )
}
